import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import messageService from '../services/messageService';
import { userService } from '../services/userService';
import firebaseConfigService from '../services/firebaseConfig';
import { collection, onSnapshot, where, query, orderBy, limit } from 'firebase/firestore';
import templateService from '../services/templateService';
import { patientService } from '../services/patientService';
import { providerService } from '../services/providerService';
import lawyerService from '../services/lawyerService';

// Resolved lazily: the tenant's database is only known after login, so a
// module-load capture would pin every tenant to (default) — TMT's data.
const getDb = () => firebaseConfigService.getServices().firestore;

// Shared group contextIds — update here if new org-wide groups are added on the backend
const SHARED_GROUP_CONTEXT_IDS = ['case_management_shared', 'marketing_alerts_shared'];

// -- Unsubscribe Trackers --
let unsubscribeFromThreadsForCase = null;
let unsubscribeFromSharedGroups = null;
let unsubscribeFromMessagesInThread = null;
let unsubscribeFromGlobalUnreadCount = null;
let unsubscribeFromDirectThreads = null;
let unsubscribeFromAiWork = null;

// -- HELPER: Sanitize Firestore Data for Redux --
// Converts Firestore Timestamps to Numbers (Milliseconds) to prevent Non-Serializable errors
const sanitizeMessage = (id, data) => {
    const sanitized = { id, ...data };

    // Helper to convert Firebase Timestamp to Millis
    const toMillis = (val) => {
        if (val && typeof val.toMillis === 'function') return val.toMillis();
        if (val && typeof val.toDate === 'function') return val.toDate().getTime();
        return val;
    };

    const timeFields = ['createdAt', 'updatedAt', 'sentAt', 'readAt', 'completedAt'];

    // 1. Sanitize top-level fields
    timeFields.forEach((field) => {
        if (sanitized[field]) {
            sanitized[field] = toMillis(sanitized[field]);
        }
    });

    // 2. Sanitize nested lastMessage (for thread lists)
    if (sanitized.lastMessage) {
        timeFields.forEach((field) => {
            if (sanitized.lastMessage[field]) {
                sanitized.lastMessage[field] = toMillis(sanitized.lastMessage[field]);
            }
        });
    }

    // 3. FIX: Sanitize nested recipients array (The cause of your error)
    if (Array.isArray(sanitized.recipients)) {
        sanitized.recipients = sanitized.recipients.map(recipient => {
            const r = { ...recipient };
            if (r.deliveryStatus) {
                // Iterate through delivery channels (in_app, sms, email, etc.)
                Object.keys(r.deliveryStatus).forEach(channel => {
                    if (r.deliveryStatus[channel]?.readAt) {
                        r.deliveryStatus[channel].readAt = toMillis(r.deliveryStatus[channel].readAt);
                    }
                    if (r.deliveryStatus[channel]?.sentAt) {
                        r.deliveryStatus[channel].sentAt = toMillis(r.deliveryStatus[channel].sentAt);
                    }
                    if (r.deliveryStatus[channel]?.deliveredAt) {
                        r.deliveryStatus[channel].deliveredAt = toMillis(r.deliveryStatus[channel].deliveredAt);
                    }
                });
            }
            return r;
        });
    }

    return sanitized;
};

const isDeletedForUser = (message, userId) => {
    if (!userId) return false;
    const deletedBy = Array.isArray(message?.deletedBy) ? message.deletedBy.map(String) : [];
    return deletedBy.includes(String(userId));
};

const sanitizeAiWorkItem = (id, data) => {
    const sanitized = { id, ...data };
    const toTimestamp = (val) => (val && typeof val.toMillis === 'function') ? val.toMillis() : val;
    ['createdAt', 'updatedAt', 'startedAt', 'completedAt', 'firestoreCreatedAt', 'firestoreUpdatedAt'].forEach(f => {
        if (sanitized[f]) sanitized[f] = toTimestamp(sanitized[f]);
    });
    return sanitized;
};

// -- THUNKS --

export const sendMessage = createAsyncThunk(
    'messaging/sendMessage',
    async (messagePayload, { dispatch, rejectWithValue }) => {
        try {
            const response = await messageService.sendMessage(messagePayload);
            dispatch(setMessageText(''));

            // Only clear recipients if we are starting a brand new thread from scratch.
            // If replying (threadId exists), we keep them to avoid UI jumping.
            if (!messagePayload.threadId) {
                dispatch(setSelectedRecipients([]));
            }

            dispatch(setShowSchedule(false));
            dispatch(setScheduledFor(''));
            dispatch(setPriority('medium'));
            dispatch(clearPendingAttachments());
            return response?.data || response;
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to send message');
        }
    }
);

export const fetchInitialData = createAsyncThunk(
    'messaging/fetchInitialData',
    async ({ caseData, userData }, { dispatch, rejectWithValue }) => {
        const {
            patientId, providerId, providerContactId, lawyerId, lawyerContactId
        } = caseData;

        const { id: currentUserId } = userData;

        try {
            // 1. Fetch all data in parallel
            const [internalRes, patientRes, providerRes, lawyerRes] = await Promise.all([
                userService.getAllInternalUsersREST(),
                patientId ? patientService.getPatientById(patientId) : Promise.resolve(null),
                providerId ? providerService.getProviderById(providerId) : Promise.resolve(null),
                lawyerId ? lawyerService.getLawyerById(lawyerId) : Promise.resolve(null),
            ]);

            // 2. Handle Internal Users
            const internalList = internalRes?.data || [];
            // We exclude current user from the list locally for selection purposes
            dispatch(setInternalUsers(internalList.filter(user => user.id !== currentUserId)));

            // 3. Process External Recipients
            const externalRecipients = [];

            // --- Handle Patient ---
            if (patientRes?.data) {
                externalRecipients.push(patientRes.data);
            }

            // --- Handle Provider (Update with selected contact) ---
            if (providerRes?.data) {
                const providerData = providerRes.data;
                let selectedContact = null;
                if (providerContactId && Array.isArray(providerData.contacts)) {
                    selectedContact = providerData.contacts.find(c => c.id === providerContactId);
                }
                externalRecipients.push({ ...providerData, contact: selectedContact || null });
            }

            // --- Handle Lawyer (Update with selected contact) ---
            if (lawyerRes?.data) {
                const lawyerData = lawyerRes.data;
                let selectedContact = null;
                if (lawyerContactId && Array.isArray(lawyerData.contacts)) {
                    selectedContact = lawyerData.contacts.find(c => c.id === lawyerContactId);
                }
                externalRecipients.push({ ...lawyerData, contact: selectedContact || null });
            }

            dispatch(setExternalRecipients(externalRecipients));
            return [];

        } catch (err) {
            console.error("[MessagingPanel] Failed to load case data:", err);
            return rejectWithValue('Failed to load case messaging data');
        }
    }
);

export const fetchUsersForMessaging = createAsyncThunk(
    'messaging/fetchUsersForMessaging',
    async (_, { rejectWithValue }) => {
        try {
            const res = await userService.getAllInternalUsersREST();
            return res?.data || [];
        } catch (err) {
            return rejectWithValue('Failed to fetch users for messaging');
        }
    }
);

/**
 * Process raw Firestore snapshots into Thread objects
 * 1. Sanitizes data (Timestamps -> Millis)
 * 2. Filters for Privacy (User must be Participant)
 * 3. Scans for Persistent Group Name
 */
const processSnapshotToThreads = (snapshot, currentUserId) => {
    // 1. Sanitize
    const msgs = snapshot.docs.map(doc => sanitizeMessage(doc.id, doc.data()));
    const threadMap = {};

    msgs.forEach((msg) => {
        if (isDeletedForUser(msg, currentUserId)) {
            return;
        }

        // 2. PRIVACY CHECK: Only include if user is a recipient OR the sender
        const isParticipant = (msg.recipientsIds && msg.recipientsIds.includes(currentUserId)) || msg.createdBy === currentUserId;

        if (isParticipant) {
            const tid = msg.threadId;
            if (!threadMap[tid]) threadMap[tid] = { messages: [], threadId: tid, persistentGroupName: null };

            threadMap[tid].messages.push(msg);

            // 3. GROUP NAME CHECK: Look for any message in the thread that has a group name
            if (msg.groupName && !threadMap[tid].persistentGroupName) {
                threadMap[tid].persistentGroupName = msg.groupName;
            }
        }
    });

    const updatedThreads = Object.values(threadMap).map((t) => {
        const sortedMessages = t.messages.sort((a, b) => b.createdAt - a.createdAt);
        const lastMessage = sortedMessages[0];
        const unreadCount = sortedMessages.filter((m) => {
            const myRecipientData = m.recipients?.find(r => r.userId === currentUserId);
            const isReadByMe = myRecipientData?.deliveryStatus?.in_app?.status === 'read';
            const isActuallyUnread = myRecipientData ? !isReadByMe : (m.status !== 'read');
            return isActuallyUnread && m.createdBy !== currentUserId;
        }).length;

        // Determine Group Name: Use persistent found in history, or fallback to last message's, or null
        const groupName = t.persistentGroupName || lastMessage?.groupName || null;

        return {
            threadId: t.threadId,
            recipients: lastMessage?.recipients || [],
            lastMessage: lastMessage,
            messageCount: sortedMessages.length,
            unreadCount: unreadCount,
            groupName: groupName, // Sent as null if not named, UI handles "Sender Name" logic
            context: lastMessage?.context || null,
            contextId: lastMessage?.contextId || null,
        };
    });

    return updatedThreads.sort((a, b) => (b.lastMessage?.createdAt || 0) - (a.lastMessage?.createdAt || 0));
};

export const listenForThreads = createAsyncThunk(
    'messaging/listenForThreads',
    async ({ caseId, currentUserId }, { dispatch, rejectWithValue }) => {
        // Validate first BEFORE unsubscribing to avoid accidentally canceling a valid listener
        if (!caseId || !currentUserId) return rejectWithValue('Missing caseId or currentUserId');

        if (unsubscribeFromThreadsForCase) unsubscribeFromThreadsForCase();
        if (unsubscribeFromSharedGroups) unsubscribeFromSharedGroups();

        // Query 1: Case-specific messages scoped to this case
        const caseQuery = query(
            collection(getDb(), 'messages'),
            where('context', 'in', ['case_internal', 'case_external']),
            where('contextId', '==', caseId)
        );

        // Query 2: Shared group messages — only where user is a member (WhatsApp-style groups)
        // Uses 'array-contains' on recipientsIds so only group members see these threads.
        // Firestore doesn't allow 'in' + 'array-contains', so we query shared groups separately.
        const sharedQuery = query(
            collection(getDb(), 'messages'),
            where('contextId', 'in', SHARED_GROUP_CONTEXT_IDS),
            where('recipientsIds', 'array-contains', currentUserId)
        );

        let caseThreads = [];
        let sharedThreads = [];

        const dispatchCombined = () => {
            const all = [...caseThreads, ...sharedThreads];
            const internal = all.filter(t => t.context === 'case_internal');
            const external = all.filter(t => t.context === 'case_external');
            dispatch(setCaseThreads({ internal, external }));
        };

        unsubscribeFromThreadsForCase = onSnapshot(caseQuery, (snapshot) => {
            caseThreads = processSnapshotToThreads(snapshot, currentUserId);
            dispatchCombined();
        }, (err) => {
            console.error('Real-time case threads error:', err);
            dispatch(setError('Failed to load case threads'));
        });

        unsubscribeFromSharedGroups = onSnapshot(sharedQuery, (snapshot) => {
            sharedThreads = processSnapshotToThreads(snapshot, currentUserId);
            dispatchCombined();
        }, (err) => {
            console.error('Real-time shared group threads error:', err);
            dispatch(setError('Failed to load shared groups'));
        });

        return true;
    }
);

export const listenForDirectThreads = createAsyncThunk(
    'messaging/listenForDirectThreads',
    async ({ userId }, { dispatch, rejectWithValue }) => {
        // Validate first BEFORE unsubscribing to avoid accidentally canceling a valid listener
        if (!userId) return rejectWithValue('Missing userId for direct threads');

        if (unsubscribeFromDirectThreads) unsubscribeFromDirectThreads();

        const q = query(collection(getDb(), 'messages'), where('context', '==', 'direct'), where('recipientsIds', 'array-contains', userId));

        unsubscribeFromDirectThreads = onSnapshot(q, (snapshot) => {
            const threads = processSnapshotToThreads(snapshot, userId);
            dispatch(setDirectThreads(threads));
        }, (err) => {
            console.error('Real-time direct threads error:', err);
            dispatch(setError('Failed to load direct messages'));
        });
        return true;
    }
);

export const listenForMessages = createAsyncThunk(
    'messaging/listenForMessages',
    async ({ threadId, currentUserId }, { dispatch, rejectWithValue }) => {
        // Validate first BEFORE unsubscribing to avoid accidentally canceling a valid listener
        if (!threadId) return rejectWithValue('Missing threadId');

        if (unsubscribeFromMessagesInThread) unsubscribeFromMessagesInThread();

        const q = query(collection(getDb(), 'messages'), where('threadId', '==', threadId), orderBy('createdAt', 'asc'));

        unsubscribeFromMessagesInThread = onSnapshot(q, (snapshot) => {
            // Sanitize messages immediately to prevent Redux Serialization Error
            const msgs = snapshot.docs
                .map((doc) => sanitizeMessage(doc.id, doc.data()))
                .filter((msg) => !isDeletedForUser(msg, currentUserId));
            dispatch(setMessages(msgs));
        }, (err) => {
            console.error('Real-time messages error:', err);
            dispatch(setMessagesError('Failed to load messages in real-time'));
        });
        return true;
    }
);

export const fetchTemplateTypes = createAsyncThunk(
    'messaging/fetchTemplateTypes',
    async (_, { rejectWithValue }) => {
        try {
            const res = await templateService.getPatientMessagesTemplate();
            return res?.data || [];
        } catch (err) {
            return rejectWithValue('Failed to load template types');
        }
    }
);

export const fetchTemplateMessage = createAsyncThunk(
    'messaging/fetchTemplateMessage',
    async ({ caseId, templateId }, { rejectWithValue }) => {
        try {
            const res = await templateService.getContextTemplate(templateId, caseId, 'case');
            return res?.data?.body || '';
        } catch (err) {
            return rejectWithValue('Failed to load template message');
        }
    }
);

export const markMessagesAsRead = createAsyncThunk(
    'messaging/markMessagesAsRead',
    async ({ userId, messageIds }, { rejectWithValue }) => {
        try {
            await messageService.markMessagesAsRead({ userId, messageIds });
            return true;
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to mark messages as read');
        }
    }
);

export const fetchUnreadMessages = createAsyncThunk(
    'messaging/fetchUnreadMessages',
    async ({ page = 1, limit = 20 } = {}, { rejectWithValue }) => {
        try {
            const response = await messageService.getUnreadMessages(page, limit);
            return response?.data || response;
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to fetch unread messages');
        }
    }
);

export const uploadMessageAttachment = createAsyncThunk(
    'messaging/uploadAttachment',
    async (file, { rejectWithValue }) => {
        try {
            const response = await messageService.uploadAttachment(file);
            return response.data; // Expecting IMessageAttachment object
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to upload attachment');
        }
    }
);

export const listenForGlobalUnreadMessages = createAsyncThunk(
    'messaging/listenForGlobalUnreadMessages',
    async ({ currentUserId }, { dispatch, getState, rejectWithValue }) => {
        // IMPORTANT: Validate first BEFORE unsubscribing to avoid accidentally canceling a valid listener
        if (!currentUserId) return rejectWithValue('Missing currentUserId');

        if (unsubscribeFromGlobalUnreadCount) unsubscribeFromGlobalUnreadCount();

        const q = query(collection(getDb(), 'messages'), where('recipientsIds', 'array-contains', currentUserId));

        unsubscribeFromGlobalUnreadCount = onSnapshot(q, (snapshot) => {
            const unreadByCaseMap = {};
            const unreadMessagesList = [];
            let totalUnreadDirect = 0;
            let totalInternalCases = 0;
            let totalExternalCases = 0;

            // Get allUsers from state for sender name lookup
            const state = getState();
            const allUsers = state.messaging?.allUsers || [];

            snapshot.docs.forEach((doc) => {
                const msg = doc.data();

                // Per-recipient read tracking
                const myRecipientData = msg.recipients?.find(r => r.userId === currentUserId);
                const isReadByMe = myRecipientData?.deliveryStatus?.in_app?.status === 'read';

                // Fallback to top-level status if recipient data not found
                const isActuallyUnread = myRecipientData ? !isReadByMe : (msg.status !== 'read');

                if (isActuallyUnread && msg.createdBy !== currentUserId && !isDeletedForUser(msg, currentUserId)) {
                    const caseId = msg.contextId;

                    // Collect individual unread message details
                    const sanitizedMsg = sanitizeMessage(doc.id, msg);

                    // Resolve sender name: check stored senderName, then lookup from allUsers
                    let senderName = sanitizedMsg.senderName || sanitizedMsg.createdByName;
                    if (!senderName && sanitizedMsg.createdBy) {
                        const senderUser = allUsers.find(u => u.id === sanitizedMsg.createdBy);
                        senderName = senderUser?.name || senderUser?.emailId?.split('@')[0] || null;
                    }

                    unreadMessagesList.push({
                        id: doc.id,
                        body: sanitizedMsg.body || '',
                        senderName: senderName || 'Unknown Sender',
                        createdBy: sanitizedMsg.createdBy,
                        contextId: caseId,
                        context: sanitizedMsg.context,
                        threadId: sanitizedMsg.threadId,
                        createdAt: sanitizedMsg.createdAt,
                    });

                    if (msg.context === 'case_internal' && caseId) {
                        if (!unreadByCaseMap[caseId]) unreadByCaseMap[caseId] = { internal: 0, external: 0 };
                        unreadByCaseMap[caseId].internal++;
                        totalInternalCases++;
                    } else if (msg.context === 'case_external' && caseId) {
                        if (!unreadByCaseMap[caseId]) unreadByCaseMap[caseId] = { internal: 0, external: 0 };
                        unreadByCaseMap[caseId].external++;
                        totalExternalCases++;
                    } else if (msg.context === 'direct') {
                        totalUnreadDirect++;
                    }
                }
            });

            // Sort by createdAt descending (newest first)
            unreadMessagesList.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

            dispatch(setUnreadCounts({
                unreadCountsByCase: unreadByCaseMap,
                totalInternalCaseUnreadCount: totalInternalCases,
                totalExternalCaseUnreadCount: totalExternalCases,
                totalDirectUnreadCount: totalUnreadDirect,
                totalUnreadCount: totalInternalCases + totalExternalCases,
                unreadMessages: unreadMessagesList,
            }));
        }, (err) => console.error('Global unread messages listener error:', err));
        return true;
    }
);

export const listenForAiWorkItems = createAsyncThunk(
    'messaging/listenForAiWorkItems',
    async ({ userId }, { dispatch, rejectWithValue }) => {
        if (!userId) return rejectWithValue('Missing userId');
        if (unsubscribeFromAiWork) unsubscribeFromAiWork();

        const q = query(
            collection(getDb(), 'ai_work_items'),
            where('userId', '==', String(userId)),
            orderBy('firestoreCreatedAt', 'desc'),
            limit(50)
        );

        unsubscribeFromAiWork = onSnapshot(q, (snapshot) => {
            const items = snapshot.docs.map(doc => sanitizeAiWorkItem(doc.id, doc.data()));
            dispatch(setAiWorkItems(items));
        }, (err) => {
            console.error('Real-time AI work listener error:', err);
        });
        return true;
    }
);

export const stopListeningForAiWorkItems = () => (_dispatch) => {
    if (unsubscribeFromAiWork) {
        unsubscribeFromAiWork();
        unsubscribeFromAiWork = null;
    }
};

export const messagingSlice = createSlice({
    name: 'messaging',
    initialState: {
        internalThreads: [],
        externalThreads: [],
        isGlobalPanelOpen: false,
        allUsers: [],
        directThreads: [],
        totalDirectUnreadCount: 0,
        selectedThread: null,
        groupName: "",
        internalUsers: [],
        externalRecipients: [],
        loading: false,
        error: null,
        newThreadDialogOpen: false,
        messages: [],
        messagesLoading: false,
        messagesError: null,
        messageText: '',
        selectedRecipients: [],
        priority: 'medium',
        sendMessageLoading: false,
        sendMessageError: null,
        sendMessageSuccess: false,
        showSchedule: false,
        scheduledFor: '',
        unreadCountsByCase: {},
        unreadMessages: [],
        totalUnreadCount: 0,
        totalInternalCaseUnreadCount: 0,
        totalExternalCaseUnreadCount: 0,
        templateTypes: [],
        selectedTemplateType: '',
        templateLoading: false,
        templateMessageError: null,
        // Attachment State
        pendingAttachments: [],
        attachmentUploading: false,
        attachmentError: null,
        unreadPagination: null,
        aiWorkItems: [],
    },
    reducers: {
        selectThread: (state, action) => {
            state.selectedThread = action.payload;
            state.selectedRecipients = []; // Reset when changing threads to avoid data bleed
            state.messageText = '';
        },
        setGroupName: (state, action) => { state.groupName = action.payload; },
        resetGroupName: (state) => { state.groupName = ""; },
        openNewThreadDialog: (state) => {
            state.newThreadDialogOpen = true;
            state.selectedRecipients = [];
        },
        openNewThreadDialogWithRecipients: (state, action) => {
            state.newThreadDialogOpen = true;
            state.selectedRecipients = [];
            if (action.payload?.groupName) {
                state.groupName = action.payload.groupName;
            }
        },
        closeNewThreadDialog: (state) => { state.newThreadDialogOpen = false; },
        setCaseThreads: (state, action) => {
            state.internalThreads = action.payload.internal;
            state.externalThreads = action.payload.external;
        },
        setLoading: (state, action) => { state.loading = action.payload; },
        setError: (state, action) => { state.error = action.payload; },
        setAiWorkItems: (state, action) => { state.aiWorkItems = action.payload; },
        setInternalUsers: (state, action) => { state.internalUsers = action.payload; },
        setExternalRecipients: (state, action) => { state.externalRecipients = action.payload; },
        setMessages: (state, action) => {
            const seen = new Set();
            state.messages = action.payload.filter(msg => {
                if (seen.has(msg.id)) return false;
                seen.add(msg.id);
                return true;
            });
        },
        setMessagesError: (state, action) => { state.messagesError = action.payload; },
        setMessageText: (state, action) => { state.messageText = action.payload; },
        setSelectedRecipients: (state, action) => { state.selectedRecipients = action.payload; },
        setPriority: (state, action) => { state.priority = action.payload; },
        setShowSchedule: (state, action) => { state.showSchedule = action.payload; },
        setScheduledFor: (state, action) => { state.scheduledFor = action.payload; },
        setUnreadCounts: (state, action) => {
            state.unreadCountsByCase = action.payload.unreadCountsByCase;
            state.unreadMessages = action.payload.unreadMessages || [];
            state.totalUnreadCount = action.payload.totalUnreadCount;
            state.totalInternalCaseUnreadCount = action.payload.totalInternalCaseUnreadCount;
            state.totalExternalCaseUnreadCount = action.payload.totalExternalCaseUnreadCount;
            state.totalDirectUnreadCount = action.payload.totalDirectUnreadCount;
        },
        setTemplateTypes: (state, action) => { state.templateTypes = action.payload; },
        setSelectedTemplateType: (state, action) => { state.selectedTemplateType = action.payload; },
        setTemplateLoading: (state, action) => { state.templateLoading = action.payload; },
        resetTemplate: (state) => { state.selectedTemplateType = ''; state.templateTypes = []; state.templateLoading = false; },
        toggleGlobalPanel: (state) => { state.isGlobalPanelOpen = !state.isGlobalPanelOpen; },
        setAllUsers: (state, action) => { state.allUsers = action.payload; },
        setDirectThreads: (state, action) => {
            const threads = action.payload;
            state.directThreads = threads;
            state.totalDirectUnreadCount = threads.reduce((sum, thread) => sum + thread.unreadCount, 0);
        },
        resetNewThreadDialog: (state) => {
            state.messageText = '';
            state.selectedRecipients = [];
            state.priority = 'medium';
            state.sendMessageLoading = false;
            state.sendMessageError = null;
            state.sendMessageSuccess = false;
            state.showSchedule = false;
            state.scheduledFor = '';
            state.groupName = '';
            state.selectedTemplateType = '';
        },
        setSendMessageLoading: (state, action) => { state.sendMessageLoading = action.payload; },
        setSendMessageError: (state, action) => { state.sendMessageError = action.payload; },
        setSendMessageSuccess: (state, action) => { state.sendMessageSuccess = action.payload; },

        // Attachment Actions
        removePendingAttachment: (state, action) => {
            state.pendingAttachments = state.pendingAttachments.filter(a => a.id !== action.payload);
        },
        clearPendingAttachments: (state) => {
            state.pendingAttachments = [];
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchUnreadMessages.fulfilled, (state, action) => {
                const payload = action.payload;
                if (Array.isArray(payload)) {
                    state.unreadMessages = payload;
                    state.totalUnreadCount = payload.length;
                    state.unreadPagination = null;
                } else if (payload?.data && Array.isArray(payload.data)) {
                    state.unreadMessages = payload.data;
                    state.unreadPagination = payload.pagination;
                    // We keep totalUnreadCount as is or update if available
                    if (payload.pagination?.totalItems !== undefined) {
                        state.totalUnreadCount = payload.pagination.totalItems;
                    }
                } else {
                    state.unreadMessages = [];
                    state.totalUnreadCount = 0;
                }
            })
            .addCase(uploadMessageAttachment.pending, (state) => { state.attachmentUploading = true; state.attachmentError = null; })
            .addCase(uploadMessageAttachment.fulfilled, (state, action) => {
                state.attachmentUploading = false;
                state.pendingAttachments.push(action.payload);
            })
            .addCase(uploadMessageAttachment.rejected, (state, action) => { state.attachmentUploading = false; state.attachmentError = action.payload; })


            .addCase(sendMessage.pending, (state) => { state.sendMessageLoading = true; state.sendMessageError = null; state.sendMessageSuccess = false; })
            .addCase(sendMessage.fulfilled, (state) => { state.sendMessageLoading = false; state.sendMessageSuccess = true; })
            .addCase(sendMessage.rejected, (state, action) => { state.sendMessageLoading = false; state.sendMessageError = action.payload; })
            .addCase(markMessagesAsRead.fulfilled, (state) => {
                state.unreadMessages = [];
                state.totalUnreadCount = 0;
                state.unreadPagination = null;
            })
            .addCase(fetchInitialData.pending, (state) => { state.loading = true; state.error = null; })
            .addCase(fetchInitialData.fulfilled, (state) => { state.loading = false; })
            .addCase(fetchInitialData.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
            .addCase(fetchUsersForMessaging.fulfilled, (state, action) => { state.allUsers = action.payload; state.internalUsers = action.payload; })
            .addCase(listenForMessages.pending, (state) => { state.messagesLoading = true; })
            .addCase(listenForMessages.fulfilled, (state) => { state.messagesLoading = false; })
            .addCase(listenForMessages.rejected, (state, action) => { state.messagesLoading = false; state.messagesError = action.payload; })
            .addCase(fetchTemplateTypes.pending, (state) => { state.templateLoading = true; })
            .addCase(fetchTemplateTypes.fulfilled, (state, action) => { state.templateLoading = false; state.templateTypes = action.payload; })
            .addCase(fetchTemplateTypes.rejected, (state, action) => { state.templateLoading = false; state.error = action.payload; })
            .addCase(fetchTemplateMessage.pending, (state) => { state.templateLoading = true; state.templateMessageError = null; })
            .addCase(fetchTemplateMessage.fulfilled, (state, action) => { state.templateLoading = false; state.messageText = action.payload; })
            .addCase(fetchTemplateMessage.rejected, (state, action) => { state.templateLoading = false; state.templateMessageError = action.payload; });
    }
});

export const stopListeningForDirectThreads = () => (_dispatch) => {
    if (unsubscribeFromDirectThreads) {
        unsubscribeFromDirectThreads();
        unsubscribeFromDirectThreads = null;
    }
};
export const stopListeningForThreads = () => (_dispatch) => {
    if (unsubscribeFromThreadsForCase) {
        unsubscribeFromThreadsForCase();
        unsubscribeFromThreadsForCase = null;
    }
    if (unsubscribeFromSharedGroups) {
        unsubscribeFromSharedGroups();
        unsubscribeFromSharedGroups = null;
    }
};
export const stopListeningForMessages = () => (_dispatch) => {
    if (unsubscribeFromMessagesInThread) {
        unsubscribeFromMessagesInThread();
        unsubscribeFromMessagesInThread = null;
    }
};
export const stopListeningForGlobalUnreadMessages = () => (_dispatch) => {
    if (unsubscribeFromGlobalUnreadCount) {
        unsubscribeFromGlobalUnreadCount();
        unsubscribeFromGlobalUnreadCount = null;
    }
};

let unsubscribeFromAllCaseThreadsInternal = null;
let unsubscribeFromAllCaseThreadsExternal = null;

export const listenForAllCaseThreads = createAsyncThunk(
    'messaging/listenForAllCaseThreads',
    async ({ currentUserId }, { dispatch, rejectWithValue }) => {
        if (!currentUserId) return rejectWithValue('Missing currentUserId');

        if (unsubscribeFromAllCaseThreadsInternal) unsubscribeFromAllCaseThreadsInternal();
        if (unsubscribeFromAllCaseThreadsExternal) unsubscribeFromAllCaseThreadsExternal();

        // Firestore doesn't support 'in' + 'array-contains' in one query,
        // so we split into two listeners scoped to the current user's messages only.
        const qInternal = query(
            collection(getDb(), 'messages'),
            where('context', '==', 'case_internal'),
            where('recipientsIds', 'array-contains', currentUserId)
        );
        const qExternal = query(
            collection(getDb(), 'messages'),
            where('context', '==', 'case_external'),
            where('recipientsIds', 'array-contains', currentUserId)
        );

        let internalThreads = [];
        let externalThreads = [];

        const dispatchCombined = () => {
            dispatch(setCaseThreads({ internal: internalThreads, external: externalThreads }));
        };

        unsubscribeFromAllCaseThreadsInternal = onSnapshot(qInternal, (snapshot) => {
            internalThreads = processSnapshotToThreads(snapshot, currentUserId);
            dispatchCombined();
        }, (err) => {
            console.error('Real-time internal case threads error:', err);
            dispatch(setError('Failed to load case threads'));
        });

        unsubscribeFromAllCaseThreadsExternal = onSnapshot(qExternal, (snapshot) => {
            externalThreads = processSnapshotToThreads(snapshot, currentUserId);
            dispatchCombined();
        }, (err) => {
            console.error('Real-time external case threads error:', err);
            dispatch(setError('Failed to load case threads'));
        });

        return true;
    }
);

export const stopListeningForAllCaseThreads = () => (_dispatch) => {
    if (unsubscribeFromAllCaseThreadsInternal) {
        unsubscribeFromAllCaseThreadsInternal();
        unsubscribeFromAllCaseThreadsInternal = null;
    }
    if (unsubscribeFromAllCaseThreadsExternal) {
        unsubscribeFromAllCaseThreadsExternal();
        unsubscribeFromAllCaseThreadsExternal = null;
    }
};

export const {
    selectThread, openNewThreadDialog, openNewThreadDialogWithRecipients, closeNewThreadDialog, setCaseThreads, setLoading,
    setError, setAiWorkItems, setInternalUsers, setExternalRecipients, setMessages, setMessagesError,
    setMessageText, setSelectedRecipients, setPriority, setShowSchedule, setScheduledFor,
    setUnreadCounts, setTemplateTypes, setSelectedTemplateType, setTemplateLoading,
    resetTemplate, toggleGlobalPanel, setAllUsers, setDirectThreads, resetNewThreadDialog,
    setGroupName, resetGroupName,
    setSendMessageLoading, setSendMessageError, setSendMessageSuccess,
    removePendingAttachment, clearPendingAttachments,
} = messagingSlice.actions;

export default messagingSlice.reducer;
